import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { useParams, Link } from "react-router-dom";
import { Radio, Play, Loader2, Users, Wifi, WifiOff, Send, DollarSign } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShareLinkCard } from "@/components/ShareLinkCard";

const API = `${BACKEND_URL}/api`;
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

/**
 * Public listener page for a live NeuralDeck broadcast.
 * Uses MediaSource Extensions (MSE) to append WebM Opus chunks streamed
 * over WebSocket from the DJ's browser via the backend.
 */
export default function LivePage() {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [connected, setConnected] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const wsRef = useRef(null);
  const chatWsRef = useRef(null);
  const queueRef = useRef([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const chatListRef = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState(500);
  const [tipMsg, setTipMsg] = useState("");
  const [tipBusy, setTipBusy] = useState(false);
  const [meta, setMeta] = useState({ tip_goal_cents: 0, raised_cents: 0, merch_items: [], slow_mode_seconds: 0 });
  const [merchIdx, setMerchIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/live/${id}/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    // Update raised total from tip_progress messages
    const last = chatMessages[chatMessages.length - 1];
    if (last?.type === "tip_progress") {
      setMeta(m => ({ ...m, raised_cents: last.raised_cents, tip_goal_cents: last.goal_cents || m.tip_goal_cents }));
    }
  }, [chatMessages]);

  const buyMerch = async (item_index = 0) => {
    try {
      const r = await axios.post(`${API}/live/merch-buy`, { session_id: id, item_index, buyer_name: user?.display_name || "anonymous" });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error("Merch checkout failed"); }
  };

  const submitTip = async () => {
    setTipBusy(true);
    try {
      const r = await axios.post(`${API}/live/tip`, {
        session_id: id,
        amount_cents: tipAmount,
        message: tipMsg,
        tipper_name: user?.display_name || "anonymous",
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tip failed");
      setTipBusy(false);
    }
  };

  useEffect(() => {
    // Open chat WS on mount (works even if user hasn't tapped Tune In)
    const wsUrl = `${WS_BASE}/api/live/chat/${id}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    const cws = new WebSocket(wsUrl);
    chatWsRef.current = cws;
    cws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setChatMessages(prev => [...prev.slice(-100), msg]);
      } catch {}
    };
    return () => { try { cws.close(); } catch {} };
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (chatListRef.current) chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
  }, [chatMessages]);

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !chatWsRef.current || chatWsRef.current.readyState !== 1) return;
    chatWsRef.current.send(chatInput.trim().slice(0, 500));
    setChatInput("");
  };

  useEffect(() => {
    axios.get(`${API}/live/${id}`).then(r => setStatus(r.data)).catch(e => setError(e.response?.data?.detail || "Live session not found"));
    const iv = setInterval(() => {
      axios.get(`${API}/live/${id}`).then(r => setStatus(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(iv);
  }, [id]);

  const start = async () => {
    if (!("MediaSource" in window)) { setError("Your browser doesn't support MediaSource playback."); return; }
    const mime = 'audio/webm; codecs="opus"';
    if (!MediaSource.isTypeSupported(mime)) { setError("Browser lacks WebM Opus support."); return; }

    const ms = new MediaSource();
    mediaSourceRef.current = ms;
    audioRef.current.src = URL.createObjectURL(ms);

    ms.addEventListener("sourceopen", () => {
      const sb = ms.addSourceBuffer(mime);
      sb.mode = "sequence";
      sourceBufferRef.current = sb;
      sb.addEventListener("updateend", flushQueue);
      openSocket();
    });

    try { await audioRef.current.play(); setPlaying(true); }
    catch (e) { console.warn("autoplay blocked", e); }
  };

  const openSocket = () => {
    const ws = new WebSocket(`${WS_BASE}/api/live/listen/${id}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onopen = () => { setConnected(true); ws.send("hello"); };
    ws.onmessage = (e) => {
      if (typeof e.data === "string") return;
      queueRef.current.push(new Uint8Array(e.data));
      flushQueue();
    };
    ws.onclose = () => { setConnected(false); };
    ws.onerror = () => { setConnected(false); };
    // keep-alive
    const ping = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); else clearInterval(ping); }, 20000);
  };

  const flushQueue = () => {
    const sb = sourceBufferRef.current;
    if (!sb || sb.updating || queueRef.current.length === 0) return;
    try { sb.appendBuffer(queueRef.current.shift()); }
    catch (e) {
      if (e.name === "QuotaExceededError") {
        // Evict everything we've buffered so far
        try { sb.remove(0, audioRef.current.currentTime - 5); } catch {}
      } else console.warn(e);
    }
  };

  useEffect(() => () => { try { wsRef.current?.close(); } catch {} }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="live-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Radio className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span></div>
        </Link>
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
          {status?.is_live ? (
            <span className="flex items-center gap-1 text-[#EF4444]"><span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse"/> LIVE</span>
          ) : (
            <span className="text-[#94A3B8]">OFFLINE</span>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16 space-y-8">
        {error ? (
          <div className="text-center space-y-4">
            <div className="font-display font-black text-3xl">Session unavailable</div>
            <div className="text-[#94A3B8]">{error}</div>
            <Link to="/" className="text-[#00F0FF] font-black uppercase tracking-widest">Back to NeuralDeck</Link>
          </div>
        ) : (
          <>
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// Live Broadcast</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl leading-tight" data-testid="live-dj-name">{status?.dj_name || "Loading…"}</h1>
              {status?.started_at && (
                <div className="text-[#94A3B8] mt-2 font-mono text-xs">Started {new Date(status.started_at).toLocaleTimeString()} · <Users className="inline w-3 h-3"/> {status.listener_count} listening</div>
              )}
            </div>

            <div className="rounded-2xl border-2 border-[#00F0FF]/50 bg-[#121620] p-8 space-y-6 shadow-[0_0_60px_rgba(0,240,255,0.2)]">
              {!playing ? (
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 rounded-full bg-[#00F0FF]/10 border-2 border-[#00F0FF] flex items-center justify-center mx-auto neon-pulse">
                    <Wifi className="w-8 h-8 text-[#00F0FF]"/>
                  </div>
                  <div className="text-sm text-[#94A3B8]">Tap to tune in. Playback starts as soon as the DJ streams the next chunk.</div>
                  <button data-testid="live-tune-in-button" onClick={start} disabled={!status?.is_live}
                    className="px-8 py-3 rounded-lg bg-[#00F0FF] text-black font-black uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-40 disabled:cursor-not-allowed">
                    <Play className="w-4 h-4 inline mr-2"/> Tune In
                  </button>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 rounded-full bg-[#00F0FF] flex items-center justify-center mx-auto">
                    <Radio className="w-9 h-9 text-black animate-pulse" strokeWidth={2.5}/>
                  </div>
                  <div className="font-display font-black text-2xl">Now streaming</div>
                  {connected ? (
                    <div className="text-xs font-mono text-[#10B981] uppercase tracking-widest flex items-center justify-center gap-2">
                      <Wifi className="w-3 h-3"/> CONNECTED
                    </div>
                  ) : (
                    <div className="text-xs font-mono text-[#EF4444] uppercase tracking-widest flex items-center justify-center gap-2">
                      <WifiOff className="w-3 h-3"/> RECONNECTING
                    </div>
                  )}
                </div>
              )}
              <audio ref={audioRef} autoPlay/>
            </div>

            {status?.latest_track && (
              <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-4" data-testid="live-current-track">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">Now playing</div>
                <div className="font-bold">{status.latest_track.title}</div>
                <div className="text-sm text-[#94A3B8]">{status.latest_track.artist}{status.latest_track.bpm ? ` · ${status.latest_track.bpm} BPM` : ""}{status.latest_track.key ? ` · ${status.latest_track.key}` : ""}</div>
              </div>
            )}

            {meta.tip_goal_cents > 0 && (
              <div className="rounded-xl border border-[#FFC107]/40 bg-[#FFC107]/5 p-4" data-testid="tip-goal-bar">
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-[#FFC107] mb-2">
                  <span>💰 TIP GOAL</span>
                  <span>${(meta.raised_cents / 100).toFixed(2)} / ${(meta.tip_goal_cents / 100).toFixed(2)}</span>
                </div>
                <div className="h-3 rounded-full bg-[#0B0E14] border border-[#FFC107]/30 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#FFC107] to-[#FF8C00] transition-all"
                    style={{ width: `${Math.min(100, (meta.raised_cents / meta.tip_goal_cents) * 100)}%` }}/>
                </div>
              </div>
            )}

            {meta.merch_items && meta.merch_items.length > 0 && (() => {
              const item = meta.merch_items[merchIdx] || meta.merch_items[0];
              const hasMultiple = meta.merch_items.length > 1;
              return (
                <div className="rounded-xl border border-[#10B981]/40 bg-[#10B981]/5 p-4" data-testid="merch-drop">
                  <div className="flex items-center gap-4">
                    {hasMultiple && (
                      <button data-testid="merch-prev" onClick={() => setMerchIdx((merchIdx - 1 + meta.merch_items.length) % meta.merch_items.length)}
                        className="text-[#10B981] hover:text-white font-black text-xl px-2">‹</button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#10B981] mb-1">
                        🛒 Merch Drop {hasMultiple && `· ${merchIdx + 1}/${meta.merch_items.length}`} <span className="text-[#94A3B8]/70">· USD</span>
                      </div>
                      <div className="font-bold truncate">{item.title}</div>
                      {item.description && <div className="text-xs text-[#94A3B8] truncate">{item.description}</div>}
                    </div>
                    <Button data-testid="merch-buy-button" onClick={() => buyMerch(merchIdx)}
                      className="bg-[#10B981] text-black hover:bg-[#34D399] font-black uppercase tracking-widest">
                      Buy ${(item.price_cents / 100).toFixed(2)} USD
                    </Button>
                    {hasMultiple && (
                      <button data-testid="merch-next" onClick={() => setMerchIdx((merchIdx + 1) % meta.merch_items.length)}
                        className="text-[#10B981] hover:text-white font-black text-xl px-2">›</button>
                    )}
                  </div>
                  {hasMultiple && (
                    <div className="flex justify-center gap-1 mt-3" data-testid="merch-dots">
                      {meta.merch_items.map((_, i) => (
                        <button key={i} onClick={() => setMerchIdx(i)} data-testid={`merch-dot-${i}`}
                          className={`w-2 h-2 rounded-full transition-colors ${i === merchIdx ? "bg-[#10B981]" : "bg-[#10B981]/30"}`}/>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Live Chat */}
            <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-4" data-testid="live-chat">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] flex items-center gap-2">
                  // Live Chat
                  {meta.slow_mode_seconds > 0 && (
                    <span data-testid="slow-mode-badge" className="text-[9px] px-2 py-0.5 rounded-full border border-[#00F0FF]/50 text-[#00F0FF]">
                      SLOW · {meta.slow_mode_seconds}s
                    </span>
                  )}
                </div>
                <button data-testid="tip-open-button" onClick={() => setTipOpen(true)}
                  className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107] hover:text-black transition-colors flex items-center gap-1">
                  <DollarSign className="w-3 h-3"/> Tip a Request
                </button>
              </div>
              <div ref={chatListRef} className="h-56 overflow-y-auto space-y-1.5 mb-3 pr-2">
                {chatMessages.length === 0 && <div className="text-xs text-[#94A3B8]">Chat is quiet. Say hi.</div>}
                {chatMessages.map((m, i) => (
                  <div key={i} data-testid={`chat-msg-${i}`} className="text-sm">
                    {m.type === "system" ? (
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#64748B]">{m.content}</div>
                    ) : m.type === "tip" ? (
                      <div className="p-2 rounded bg-gradient-to-r from-[#FFC107]/20 to-[#FF8C00]/20 border border-[#FFC107]/40" data-testid={`tip-msg-${i}`}>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107] mb-0.5">
                          💰 TIP · ${(m.amount_cents / 100).toFixed(2)} from {m.user_name}
                        </div>
                        {m.content && <div className="text-sm text-[#F8FAFC]">"{m.content}"</div>}
                      </div>
                    ) : (
                      <div>
                        <span className={`font-bold ${m.is_dj ? "text-[#FF2A6D]" : "text-[#00F0FF]"}`}>{m.user_name}{m.is_dj && " ★"}</span>
                        <span className="text-[#94A3B8]">: </span>
                        <span className="text-[#F8FAFC]">{m.content}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={sendChat} className="flex gap-2">
                <Input data-testid="chat-input" value={chatInput} onChange={e => setChatInput(e.target.value)}
                  placeholder={user ? "Say something…" : "Log in for a name (or chat as guest)"}
                  maxLength={500}
                  className="bg-[#0B0E14] border-[#2A354B] text-white"/>
                <Button data-testid="chat-send" type="submit" disabled={!chatInput.trim()}
                  className="bg-[#00F0FF] text-black hover:bg-[#33F3FF]"><Send className="w-4 h-4"/></Button>
              </form>
            </div>
          </>
        )}
        <ShareLinkCard title="Share this live set" />
      </main>

      <Dialog open={tipOpen} onOpenChange={setTipOpen}>
        <DialogContent className="bg-[#121620] border-[#FFC107]/40 text-white sm:max-w-md" data-testid="tip-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-[#FFC107]"/> Tip a Request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm text-[#94A3B8]">Support {status?.dj_name || "the DJ"} and add a track request or shout-out. <span className="text-[#00F0FF] font-black">All amounts in USD.</span></div>
            <div className="grid grid-cols-4 gap-2" data-testid="tip-preset-grid">
              {[100, 500, 1000, 2000, 5000].map(a => (
                <button key={a} data-testid={`tip-amount-${a}`} onClick={() => setTipAmount(a)}
                  className={`py-3 rounded-lg font-display font-black text-lg transition-colors ${
                    tipAmount === a ? "bg-[#FFC107] text-black" : "bg-transparent border border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107]/10"
                  }`}>${a / 100}</button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FFC107] font-black text-lg pointer-events-none">$</span>
              <Input data-testid="tip-custom-amount" type="number" min="1" step="1"
                value={(tipAmount / 100).toString()}
                onChange={e => {
                  const dollars = parseFloat(e.target.value || "0");
                  if (dollars >= 1) setTipAmount(Math.round(dollars * 100));
                }}
                placeholder="Custom amount ($1 or more)"
                className="bg-[#0B0E14] border-[#FFC107]/50 text-white pl-7 font-display font-black text-lg"/>
            </div>
            <div className={`p-2 rounded-lg border text-xs text-center ${tipAmount >= 1000
                ? "border-[#00FF66]/40 bg-[#00FF66]/5"
                : "border-[#FFC107]/30 bg-[#FFC107]/5"}`} data-testid="tip-tier-badge">
              {tipAmount >= 5000 ? (
                <><span className="font-black text-[#00FF66]">✓ Guaranteed</span> · 4 song requests · DJ must honour</>
              ) : tipAmount >= 2000 ? (
                <><span className="font-black text-[#00FF66]">✓ Guaranteed</span> · 2 song requests · DJ must honour</>
              ) : tipAmount >= 1000 ? (
                <><span className="font-black text-[#00FF66]">✓ Guaranteed</span> · 1 song request · DJ must honour</>
              ) : (
                <><span className="font-black text-[#FFC107]">DJ discretion</span> · Under $10 = shout-out only</>
              )}
            </div>
            <Input data-testid="tip-message-input" value={tipMsg} onChange={e => setTipMsg(e.target.value)}
              placeholder="Your request or shout-out (optional)…" maxLength={200}
              className="bg-[#0B0E14] border-[#2A354B] text-white"/>
            <Button data-testid="tip-checkout-button" onClick={submitTip} disabled={tipBusy}
              className="w-full bg-[#FFC107] text-black hover:bg-[#FFD54F] font-black uppercase tracking-widest">
              {tipBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : `Send $${tipAmount / 100} USD via Stripe`}
            </Button>
            <div className="text-[11px] text-center text-[#94A3B8] font-mono">Charged in <span className="text-[#00F0FF] font-black">USD</span> · Your bank auto-converts on statement · Test card 4242 4242 4242 4242</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
